
import React from "react";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Users, BookOpen, UserCheck, LineChart, FileText, HardDrive } from "lucide-react";

interface SummaryCardsProps {
  userCount: number;
  clientCount: number;
  therapistCount: number;
  adminCount: number;
  unassignedCount: number;
  journalCount: number;
  averageJournalsPerClient: number;
  connectionRequests: number;
  clientToTherapistRatio: number;
  pageHits: number;
  // New file metrics
  totalUploads?: number;
  totalStorageMB?: number;
  averageUploadsPerClient?: number;
  averageStoragePerClientMB?: number;
}

const SummaryCards = ({
  userCount,
  clientCount,
  therapistCount,
  adminCount,
  unassignedCount,
  journalCount,
  averageJournalsPerClient,
  connectionRequests,
  clientToTherapistRatio,
  pageHits,
  // New file metrics
  totalUploads = 0,
  totalStorageMB = 0,
  averageUploadsPerClient = 0,
  averageStoragePerClientMB = 0
}: SummaryCardsProps) => {
  // Format the client-therapist ratio to display correctly
  const formattedRatio = clientToTherapistRatio ? `${clientToTherapistRatio}:1` : 'N/A';
  
  // Create a properly formatted breakdown of user types
  const userBreakdownText = `${clientCount} clients, ${therapistCount} therapists${adminCount > 0 ? `, ${adminCount} admins` : ''}${unassignedCount > 0 ? `, ${unassignedCount} unassigned` : ''}`;
  
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{userCount}</div>
            <p className="text-xs text-muted-foreground">
              {userBreakdownText}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Journal Entries</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{journalCount}</div>
            <p className="text-xs text-muted-foreground">
              ~{averageJournalsPerClient} entries per client
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Connections</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{connectionRequests}</div>
            <p className="text-xs text-muted-foreground">
              Client-Therapist ratio: {formattedRatio}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Page Views</CardTitle>
            <LineChart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pageHits}</div>
            <p className="text-xs text-muted-foreground">
              Estimated total interactions
            </p>
          </CardContent>
        </Card>
      </div>
      
      {/* New section for file metrics */}
      <h3 className="text-lg font-medium mt-6 mb-3 text-[#333B44]">File Storage Metrics</h3>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Uploads</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUploads}</div>
            <p className="text-xs text-muted-foreground">
              File attachments stored
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Storage</CardTitle>
            <HardDrive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalStorageMB.toFixed(2)} MB</div>
            <p className="text-xs text-muted-foreground">
              Total file storage used
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Uploads Per Client</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageUploadsPerClient.toFixed(1)}</div>
            <p className="text-xs text-muted-foreground">
              Average uploads per client
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Storage Per Client</CardTitle>
            <HardDrive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageStoragePerClientMB.toFixed(2)} MB</div>
            <p className="text-xs text-muted-foreground">
              Average storage per client
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default SummaryCards;
